import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr, checkoutOriginUrl, goToCheckout } from "../api";
import { isNative } from "../mobileAuth";
import { useAuth } from "../AuthContext";
import { Page, SectionTitle } from "../components/Layout";
import { GraduationCap, ChevronLeft, Users, CalendarDays, Clock, LifeBuoy, MessageSquare, Award } from "lucide-react";

const BE = process.env.REACT_APP_BACKEND_URL;
const fileHref = (u) => (!u ? "#" : u.startsWith("http") ? u : `${BE}${u}`);
const eur = (n) => (Number(n) > 0 ? `${Number(n).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €` : "Gratuito");
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : null);

export default function AcademyPublic() {
  const { cid } = useParams();
  return cid ? <CourseDetail cid={cid} /> : <CoursesList />;
}

/* ============================ LIST ============================ */
function CoursesList() {
  const nav = useNavigate();
  const [courses, setCourses] = useState(null);
  useEffect(() => {
    document.title = "MAS Academy — Corsi professionali";
    api.get("/academy/public/courses").then((r) => setCourses(r.data)).catch((e) => { toast.error(apiErr(e)); setCourses([]); });
  }, []);
  return (
    <Page>
      <SectionTitle sub="Corsi professionali erogati da MAS Academy con docenti selezionati.">MAS Academy</SectionTitle>
      {courses === null ? <div className="text-center text-gray-400 pt-10 text-sm">Carico…</div> : (
        <div data-testid="academy-public-list" className="space-y-4">
          {courses.length === 0 && <p className="text-sm text-gray-400" data-testid="academy-public-empty">Nessun corso Academy in programma al momento.</p>}
          {courses.map((c) => (
            <button key={c.id} data-testid={`academy-public-card-${c.id}`} onClick={() => nav(`/academy/${c.id}`)} className="card overflow-hidden text-left w-full">
              {c.image ? <img src={fileHref(c.image)} alt={c.title} className="w-full h-40 object-cover" /> :
                <div className="w-full h-28 bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center text-white"><GraduationCap size={34} /></div>}
              <div className="p-4">
                <div className="flex items-center gap-2 text-violet-700 mb-1"><GraduationCap size={14} /><span className="text-[11px] font-semibold">Corso erogato da MAS Academy</span></div>
                <div className="font-semibold text-sm">{c.title}</div>
                <div className="text-[11px] text-gray-500 mt-0.5">
                  {c.category} · {c.duration_days ? `${c.duration_days} giornate` : ""}{fmtD(c.date) ? ` · dal ${fmtD(c.date)}` : ""}
                </div>
                {c.branding?.teacher?.display_name && <div className="text-[11px] text-gray-500 mt-0.5">Docente: {c.branding.teacher.display_name}</div>}
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm font-bold text-gray-900">{eur(c.price)}</span>
                  <span className={`text-[11px] ${c.seats_left > 0 ? "text-green-600" : "text-red-500"}`}>{c.seats_left > 0 ? `${c.seats_left} posti disponibili` : "Posti esauriti"}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </Page>
  );
}

/* ============================ DETAIL ============================ */
const setMeta = (key, content, attr = "name") => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

function CourseDetail({ cid }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/academy/public/courses/${cid}`).then((r) => setC(r.data)).catch((e) => { toast.error(apiErr(e)); setC(false); });
  }, [cid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!c) return;
    const desc = (c.description || "").slice(0, 160);
    document.title = `${c.title} — MAS Academy`;
    setMeta("description", desc);
    setMeta("og:title", `${c.title} — MAS Academy`, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:type", "website", "property");
    if (c.image) setMeta("og:image", fileHref(c.image), "property");
  }, [c]);

  const enroll = async () => {
    if (!user) { nav(`/registrati?role=customer&next=/academy/${cid}`); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/academy/courses/${cid}/enroll`, { origin_url: checkoutOriginUrl(), native: isNative() });
      if (data.checkout_url) goToCheckout(data.checkout_url);
      else toast.error("Impossibile avviare il pagamento. Riprova.");
    } catch (e) {
      const st = e?.response?.status;
      if (st === 409) { toast.info("Risulti già iscritto a questo corso."); nav("/allievo"); }
      else toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  if (c === null) return <Page><div className="text-center text-gray-400 pt-16 text-sm">Carico…</div></Page>;
  if (c === false) return <Page><div className="text-center text-gray-500 pt-16">Corso non trovato.</div></Page>;
  const t = c.branding?.teacher;
  const soldOut = c.seats_left <= 0;

  return (
    <Page>
      <button data-testid="academy-public-back" onClick={() => nav("/academy")} className="inline-flex items-center gap-1 text-sm text-violet-700 mb-3"><ChevronLeft size={16} /> Tutti i corsi</button>
      <div data-testid="academy-public-detail" className="card overflow-hidden mb-4">
        {c.image ? <img src={fileHref(c.image)} alt={c.title} className="w-full h-48 object-cover" /> :
          <div className="w-full h-32 bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center text-white"><GraduationCap size={40} /></div>}
        <div className="p-4">
          <div className="flex items-center gap-2 text-violet-700 mb-1"><GraduationCap size={14} /><span className="text-[11px] font-semibold">Corso erogato da MAS Academy</span></div>
          <h1 className="text-lg font-bold">{c.title}</h1>
          <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
            <span>{c.category}</span>
            {c.duration_days ? <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {c.duration_days} giornate</span> : null}
            {fmtD(c.date) ? <span className="inline-flex items-center gap-1"><Clock size={12} /> dal {fmtD(c.date)}{c.time ? ` · ${c.time}` : ""}</span> : null}
          </div>
        </div>
      </div>

      {t && (
        <div className="card p-4 mb-4" data-testid="academy-public-teacher">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">Docente</div>
          <div className="flex items-center gap-3">
            {t.logo_url && <img src={fileHref(t.logo_url)} alt="logo docente" className="w-12 h-12 rounded-xl object-cover border" />}
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{t.display_name}</div>
              {t.website_url && <a href={t.website_url} target="_blank" rel="noreferrer" className="text-[11px] text-violet-700 truncate block">{t.website_url}</a>}
              {(t.links || []).map((l, i) => l.url ? <a key={i} href={l.url} target="_blank" rel="noreferrer" className="text-[11px] text-violet-700 mr-2">{l.label || l.url}</a> : null)}
            </div>
          </div>
          {t.bio && <p className="text-xs text-gray-600 mt-2">{t.bio}</p>}
        </div>
      )}

      {c.description && <div className="card p-4 mb-4"><div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Descrizione</div><p className="text-sm text-gray-700 whitespace-pre-wrap">{c.description}</p></div>}

      {(c.days || []).length > 0 && (
        <div className="card p-4 mb-4" data-testid="academy-public-program">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">Programma</div>
          <div className="space-y-2">
            {c.days.map((d, i) => (
              <div key={i} className="border-l-2 border-violet-200 pl-3">
                <div className="text-sm font-medium">Giornata {(d.day_index ?? i) + 1} · {d.title}</div>
                {d.program && <div className="text-xs text-gray-600 mt-0.5">{d.program}</div>}
                {!d.program && d.description && <div className="text-xs text-gray-600 mt-0.5">{d.description}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 mb-4">
        {c.tutoring_enabled && (
          <div className="card p-4 flex items-start gap-3" data-testid="academy-public-tutoring">
            <MessageSquare size={18} className="text-violet-600 shrink-0 mt-0.5" />
            <div><div className="text-sm font-medium">Tutoraggio iniziale incluso</div><div className="text-xs text-gray-500">Supporto privato con il docente {c.tutoring_duration_days ? `per ${c.tutoring_duration_days} giorni` : ""} dopo il corso.</div></div>
          </div>
        )}
        {c.level_up_enabled && (
          <div className="card p-4 flex items-start gap-3">
            <Award size={18} className="text-violet-600 shrink-0 mt-0.5" />
            <div><div className="text-sm font-medium">Attestato Level Up</div><div className="text-xs text-gray-500">Rilasciato al completamento di tutti gli esercizi richiesti.</div></div>
          </div>
        )}
        {c.assistance_enabled && (
          <div className="card p-4 flex items-start gap-3 bg-violet-50/40" data-testid="academy-public-assistance">
            <LifeBuoy size={18} className="text-violet-600 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-medium">Assistenza post-corso (servizio opzionale)</div>
              <div className="text-xs text-gray-500">{eur(c.assistance_price)}/mese · attivabile dall'Area Allievo dopo l'iscrizione. Non inclusa nel prezzo del corso.</div>
            </div>
          </div>
        )}
      </div>

      <div className="card p-4 sticky bottom-24 shadow-lg">
        <div className="flex items-center justify-between mb-2">
          <div><div className="text-[11px] text-gray-400">Prezzo corso</div><div className="text-xl font-bold text-gray-900">{eur(c.price)}</div></div>
          <span className={`text-xs inline-flex items-center gap-1 ${soldOut ? "text-red-500" : "text-green-600"}`}><Users size={13} /> {soldOut ? "Posti esauriti" : `${c.seats_left} posti`}</span>
        </div>
        <button data-testid="academy-enroll-btn" disabled={busy || soldOut || c.ended} onClick={enroll} className="btn-primary w-full disabled:opacity-50">
          {c.ended ? "Iscrizioni chiuse" : soldOut ? "Posti esauriti" : busy ? "Attendere…" : "Iscriviti al corso"}
        </button>
        {!user && !soldOut && !c.ended && <p className="text-[11px] text-gray-400 text-center mt-2">Per iscriverti è richiesto un account gratuito. Ti riporteremo qui dopo la registrazione.</p>}
      </div>
    </Page>
  );
}
